import React, { useContext, useState, useEffect, useRef } from "react";
import { AuthContext } from "../../context/AuthContext";
import { fichar, getFichajes, BACKEND_URL } from "../../api/api";
import CalendarioFichajes from "../calendario/CalendarioFichajes";
import SolicitudesTrabajador from "../trabajador/SolicitudesTrabajador";
import FichajesOfflineStatus from "../trabajador/FichajesOfflineStatus";

/**
 * Componente de panel de fichaje para trabajadores.
 * Permite registrar entradas/salidas, visualizar fichajes del día, calcular tiempos trabajados
 * y acceder a solicitudes de ausencias/vacaciones.
 * Incluye validaciones de jornadas y calendario de fichajes.
 * 
 * @returns {JSX.Element} Panel completo de fichaje con controles y calendario
 */
export default function PanelFichaje() {
  const { token, user, reloadUser } = useContext(AuthContext);
  const [mensajeExito, setMensajeExito] = useState("");
  const [mensajeError, setMensajeError] = useState("");
  const [showSolicitudes, setShowSolicitudes] = useState(false);
  const [solicitudesViewMode, setSolicitudesViewMode] = useState("both");

  const [fichajes, setFichajes] = useState([]);
  const [fechaFiltro, setFechaFiltro] = useState(() => {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tiempoTrabajadoMin, setTiempoTrabajadoMin] = useState(0);
  const [tiempoRestanteMin, setTiempoRestanteMin] = useState(0);

  const [puedeFicharEntrada, setPuedeFicharEntrada] = useState(true);
  const [puedeFicharSalida, setPuedeFicharSalida] = useState(false);
  const [confirmarEntrada, setConfirmarEntrada] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const [jornadaRegistrada, setJornadaRegistrada] = useState(false);
  const [sincronizandoPendientes, setSincronizandoPendientes] = useState(false);
  const syncingRef = useRef(false);

  const timeoutEntrada = useRef(null);
  const timeoutSalida = useRef(null);

  const cargarFichajes = async (fechaISO) => {
    try {
      setError("");
      setLoading(true);
      setFichajes([]);

      if (!token) throw new Error("No autorizado");

      const d = new Date(fechaISO);
      if (isNaN(d.getTime())) throw new Error("Fecha inválida");

      const anio = d.getFullYear();
      const mes = d.getMonth() + 1;
      const dia = d.getDate();

      const query = `?anio=${anio}&mes=${mes}&dia=${dia}`;
      
      const data = await getFichajes(token, query);

      // Convertir a array si es un objeto pseudo-array
      let dataArray;
      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data && typeof data === 'object') {
        const keys = Object.keys(data).filter(k => !isNaN(parseInt(k)));
        dataArray = keys.map(k => data[k]);
      } else {
        dataArray = [];
      }

      // Solo los fichajes de este usuario
      const misFichajes = dataArray.filter(
        (f) => String(f.userId?._id) === String(user?._id)
      );
      
      // IMPORTANTE: Añadir fichajes pendientes de localStorage para mantener estado correcto
      const pendientesStr = localStorage.getItem('fichajes-pendientes');
      if (pendientesStr) {
        try {
          const todosPendientes = JSON.parse(pendientesStr);
          const misPendientes = todosPendientes.filter(f => f.token === token);
          
          // Convertir fichajes pendientes al formato correcto y filtrar por fecha actual
          const fichajesPendientesHoy = misPendientes
            .filter(fp => {
              const fpDate = new Date(fp.timestamp);
              const today = new Date(fechaISO);
              return fpDate.getFullYear() === today.getFullYear() &&
                     fpDate.getMonth() === today.getMonth() &&
                     fpDate.getDate() === today.getDate();
            })
            .map(fp => ({
              tipo: fp.tipo,
              fecha: fp.timestamp,
              userId: { _id: user._id },
              _id: fp.uuid,
              offline: true // Marcador para identificar fichajes offline
            }));
          
          // Combinar fichajes del backend con los pendientes
          misFichajes.push(...fichajesPendientesHoy);
        } catch (error) {
          console.error('Error al procesar fichajes pendientes:', error);
        }
      }
      
      setFichajes(misFichajes);

      // Filtrar fichajes SOLO del día actual para cálculos
      const hoyParaComparar = new Date(fechaISO);
      const fichajesDelDia = misFichajes.filter(f => {
        const fechaFichaje = new Date(f.fecha);
        return fechaFichaje.getFullYear() === hoyParaComparar.getFullYear() &&
               fechaFichaje.getMonth() === hoyParaComparar.getMonth() &&
               fechaFichaje.getDate() === hoyParaComparar.getDate();
      });

      // Ordenar fichajes cronológicamente
      const fichajesOrdenados = [...fichajesDelDia].sort(
        (a, b) => new Date(a.fecha) - new Date(b.fecha)
      );

      // Obtener jornadas efectivas
      let jornadasForCalc = user?.jornadasEffective || [];
      if (!jornadasForCalc || jornadasForCalc.length === 0) {
        jornadasForCalc = user?.jornadas || [];
      }

      const jornadasCount = jornadasForCalc.length || 1;
      const maxFichajes = jornadasCount;

      // Puede fichar entrada si: NO hay entrada abierta Y no ha alcanzado el máximo de pares
      const puedeEntrada = entradasAbiertas === 0 && paresCompletados < maxFichajes;
      setPuedeFicharEntrada(puedeEntrada);

      // Puede fichar salida si: hay una entrada abierta
      const puedeSalida = entradasAbiertas > 0;
      setPuedeFicharSalida(puedeSalida);

      // Jornada completa si: ha completado todos los pares Y no hay entradas abiertas
      const jornadaCompleta = jornadasCount > 0 && paresCompletados >= maxFichajes && entradasAbiertas === 0;
      setJornadaRegistrada(jornadaCompleta);

      // CALCULAR TIEMPO TRABAJADO
      let trabajadoMs = 0;
      let ultimaEntrada = null;

      fichajesOrdenados.forEach((f) => {
        if (f.tipo === "entrada") {
          ultimaEntrada = new Date(f.fecha).getTime();
        } else if (f.tipo === "salida" && ultimaEntrada !== null) {
          const salidaTs = new Date(f.fecha).getTime();
          if (salidaTs > ultimaEntrada) {
            trabajadoMs += salidaTs - ultimaEntrada;
          }
          ultimaEntrada = null;
        }
      });

      // Si hay una entrada abierta (sin salida), sumar tiempo hasta ahora
      // PERO SOLO SI LA ENTRADA ES DE HOY, no de días anteriores
      if (ultimaEntrada !== null) {
        const entradaDate = new Date(ultimaEntrada);
        const hoy = new Date(fechaISO);
        const esHoy = entradaDate.getFullYear() === hoy.getFullYear() &&
                      entradaDate.getMonth() === hoy.getMonth() &&
                      entradaDate.getDate() === hoy.getDate();
        
        if (esHoy) {
          // Solo sumar si la entrada es de hoy
          const ahora = Date.now();
          const finDelDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999).getTime();
          const limite = Math.min(ahora, finDelDia);
          trabajadoMs += limite - ultimaEntrada;
        }
        // Si la entrada es de otro día, NO sumar nada (fichaje incompleto)
      }

      const trabajadoMin = Math.floor(trabajadoMs / 60000);
      setTiempoTrabajadoMin(trabajadoMin);

      // Calcular tiempo esperado según jornadas para el día de hoy
      const todayIndex = new Date().getDay();

      const mapNameToIndex = (s) => {
        if (s === undefined || s === null) return null;
        const key = String(s).trim().toLowerCase();
        if (/^[0-6]$/.test(key)) return parseInt(key, 10);
        if (/^[1-7]$/.test(key)) return parseInt(key, 10) % 7;
        const map = {
          domingo: 0, dom: 0, sun: 0,
          lunes: 1, lun: 1, mon: 1,
          martes: 2, mar: 2, tue: 2,
          miércoles: 3, miercoles: 3, mié: 3, mie: 3, wed: 3,
          jueves: 4, jue: 4, thu: 4,
          viernes: 5, vie: 5, fri: 5,
          sábado: 6, sabado: 6, sáb: 6, sab: 6, sat: 6,
        };
        return map[key] ?? null;
      };

      const isDiaMatch = (diaField, todayIdx) => {
        if (diaField === undefined || diaField === null) return false;
        const raw = String(diaField).toLowerCase();
        const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
        for (const part of parts) {
          const norm = part.replace(/\s+a\s+|\s+to\s+|\s*-\s*/g, "-").trim();
          if (norm.includes("-")) {
            const [startRaw, endRaw] = norm.split("-").map((x) => x.trim()).filter(Boolean);
            const start = mapNameToIndex(startRaw);
            const end = mapNameToIndex(endRaw);
            if (start === null || end === null) continue;
            if (start <= end) {
              if (todayIdx >= start && todayIdx <= end) return true;
            } else {
              if (todayIdx >= start || todayIdx <= end) return true;
            }
          } else {
            const idx = mapNameToIndex(norm);
            if (idx !== null && idx === todayIdx) return true;
          }
        }
        return false;
      };

      let esperadoMin = 0;
      if (jornadasForCalc && Array.isArray(jornadasForCalc)) {
        jornadasForCalc.forEach((j) => {
          if (!j || !j.horario) return;
          j.horario.forEach((h) => {
            if (!h) return;
            const match = isDiaMatch(h.dia, todayIndex);
            if (!match) return;
            const [hiH, hiM] = (h.horaInicio || "00:00").split(":").map(Number);
            const [hfH, hfM] = (h.horaFin || "00:00").split(":").map(Number);
            const minutosSegmento = hfH * 60 + hfM - (hiH * 60 + hiM);
            if (!isNaN(minutosSegmento) && minutosSegmento > 0) esperadoMin += minutosSegmento;
          });
        });
      }
      setTiempoRestanteMin(Math.max(0, esperadoMin - trabajadoMin));

    } catch (err) {
      setError("Error al cargar fichajes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarFichajes(fechaFiltro);
  }, [fechaFiltro, token]);

  // Resetear estado de jornada cuando cambia la fecha
  useEffect(() => {
    setJornadaRegistrada(false);
    setPuedeFicharEntrada(true);
    setPuedeFicharSalida(false);
  }, [fechaFiltro]);

  // Función de sincronización (disponible para botón manual)
  const sincronizarPendientes = async () => {
    if (!token || !navigator.onLine || !user?._id) return;
    if (syncingRef.current) return;

    syncingRef.current = true;
    setSincronizandoPendientes(true);

    try {
      const pendientesStr = localStorage.getItem('fichajes-pendientes');
      if (!pendientesStr) return;

      const todosPendientes = JSON.parse(pendientesStr);
      const misPendientes = todosPendientes.filter(f => f.userId === user._id);
      const otrosPendientes = todosPendientes.filter(f => f.userId !== user._id);

      if (misPendientes.length === 0) return;

      let sincronizados = 0;
      let errores = 0;
      const fichajesFallidos = [];
      
      for (const fichajePendiente of misPendientes) {
        try {
          const payload = {
            timestamp: fichajePendiente.timestamp,
            offline: true,
            uuid: fichajePendiente.uuid,
            clientTimestamp: fichajePendiente.timestamp
          };

          const resultado = await fichar(token, fichajePendiente.tipo, payload);
          sincronizados++;
        } catch (error) {
          errores++;
          fichajesFallidos.push({ ...fichajePendiente, retryCount: (fichajePendiente.retryCount || 0) + 1 });
        }
      }

      // Solo eliminar los sincronizados correctamente, mantener los fallidos para reintentar
      const pendientesRestantes = [...otrosPendientes, ...fichajesFallidos];
      if (pendientesRestantes.length > 0) {
        localStorage.setItem('fichajes-pendientes', JSON.stringify(pendientesRestantes));
      } else {
        localStorage.removeItem('fichajes-pendientes');
      }

      if (sincronizados > 0) {
        setMensajeExito(`✅ ${sincronizados} fichaje(s) sincronizado(s) correctamente`);
        setTimeout(() => setMensajeExito(""), 5000);
        await cargarFichajes(fechaFiltro);
        await reloadUser();
      }

      if (errores > 0) {
        setMensajeError(`⚠️ ${errores} fichaje(s) no se pudieron sincronizar. Se reintentará.`);
        setTimeout(() => setMensajeError(""), 5000);
      }
    } catch (error) {
      // sync error silencioso
    } finally {
      syncingRef.current = false;
      setSincronizandoPendientes(false);
    }
  };

  // Sincronizar fichajes pendientes: múltiples triggers para móvil
  useEffect(() => {
    const handleOnline = () => {
      sincronizarPendientes();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && navigator.onLine && token && user?._id) {
        sincronizarPendientes();
      }
    };

    const handleConnectionChange = () => {
      if (navigator.onLine && token && user?._id) {
        sincronizarPendientes();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    if (navigator.connection) {
      navigator.connection.addEventListener('change', handleConnectionChange);
    }

    // Reintento periódico cada 30s (backup para cuando los eventos no se disparan)
    const intervalId = setInterval(() => {
      if (navigator.onLine && token && user?._id) {
        const pendientesStr = localStorage.getItem('fichajes-pendientes');
        if (pendientesStr) {
          try {
            const pendientes = JSON.parse(pendientesStr);
            if (pendientes.some(f => f.userId === user._id)) {
              sincronizarPendientes();
            }
          } catch (e) { /* ignore */ }
        }
      }
    }, 30000);

    // Sincronizar al cargar si hay conexión y tenemos usuario
    if (navigator.onLine && token && user?._id) {
      sincronizarPendientes();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (navigator.connection) {
        navigator.connection.removeEventListener('change', handleConnectionChange);
      }
      clearInterval(intervalId);
    };
  }, [token, user?._id]);

  useEffect(() => {
    return () => {
      if (timeoutEntrada.current) clearTimeout(timeoutEntrada.current);
      if (timeoutSalida.current) clearTimeout(timeoutSalida.current);
    };
  }, []);

  const handleFichar = async (tipo) => {
    setMensajeError("");
    setMensajeExito("");
    setConfirmarEntrada(false);
    setConfirmarSalida(false);

    if (timeoutEntrada.current) clearTimeout(timeoutEntrada.current);
    if (timeoutSalida.current) clearTimeout(timeoutSalida.current);

    // PRIMERO: Si hay pendientes offline, sincronizarlos antes de hacer nada
    if (user?._id) {
      try {
        const pendientesStr = localStorage.getItem('fichajes-pendientes');
        if (pendientesStr) {
          const pendientes = JSON.parse(pendientesStr);
          const misPendientes = pendientes.filter(f => f.userId === user._id);
          // Solo bloquear si hay pendientes con menos de 3 reintentos
          const sincronizables = misPendientes.filter(f => (f.retryCount || 0) < 3);
          if (sincronizables.length > 0) {
            setMensajeExito(`🔄 Sincronizando ${sincronizables.length} fichaje(s) offline pendiente(s)...`);
            await sincronizarPendientes();
            // Verificar si la sincronización tuvo éxito
            const afterSync = localStorage.getItem('fichajes-pendientes');
            const stillPending = afterSync ? JSON.parse(afterSync).filter(f => f.userId === user._id && (f.retryCount || 0) < 3).length : 0;
            if (stillPending === 0) {
              await cargarFichajes(fechaFiltro);
              return;
            }
            // Si sigue fallando, avisar pero permitir continuar con fichaje normal
            setMensajeError(`⚠️ ${stillPending} fichaje(s) pendiente(s) no se pudieron sincronizar. Se reintentará automáticamente.`);
            setTimeout(() => setMensajeError(""), 5000);
          }
        }
      } catch (e) {
        console.error('Error chequeando pendientes:', e);
      }
    }

    // Capturar la hora AHORA (antes de enviar), para usarla si falla la red
    const horaLocal = new Date();
    const clientTimestamp = horaLocal.toISOString();

    // Si ya sabemos que estamos offline, guardar directamente sin esperar timeout
    if (!navigator.onLine) {
      guardarOffline(tipo, clientTimestamp);
      return;
    }

    // Intentar fichaje con timeout (6 segundos).
    // Si el móvil reporta online pero no tiene red real, el timeout lo detectará.
    try {
      const res = await fichar(token, tipo, { clientTimestamp, timeout: 6000 });

      if (res.mensaje) {
        setMensajeExito(res.mensaje);
        setTimeout(() => setMensajeExito(""), 4000);
      }

      await cargarFichajes(fechaFiltro);
      await reloadUser();
    } catch (err) {
      // Detectar cualquier tipo de error de red (la lista varía por navegador/SO)
      const statusCode = err.response?.status;
      const esErrorRed = !navigator.onLine
        || err.code === 'ECONNABORTED'
        || err.code === 'ERR_NETWORK'
        || err.code === 'ERR_CANCELED'
        || !err.response
        || statusCode === 0
        || statusCode === 503
        || statusCode === 504
        || err.message?.toLowerCase().includes('timeout')
        || err.message?.toLowerCase().includes('network')
        || err.message?.toLowerCase().includes('internet')
        || err.message?.toLowerCase().includes('connection')
        || err.message?.toLowerCase().includes('fetch');

      if (esErrorRed) {
        guardarOffline(tipo, clientTimestamp);
      } else {
        // Error del servidor (400, 500, etc.) — no es problema de red
        setMensajeError(err?.response?.data?.message || "Error al fichar. Inténtalo de nuevo.");
        setTimeout(() => setMensajeError(""), 4000);
      }
    }
  };

  const guardarOffline = (tipo, clientTimestamp) => {
    try {
      const fichajeOffline = {
        tipo,
        timestamp: clientTimestamp,
        userId: user._id,
        token,
        uuid: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        synced: false,
        createdAt: Date.now()
      };
      const pendientesStr = localStorage.getItem('fichajes-pendientes');
      const pendientes = pendientesStr ? JSON.parse(pendientesStr) : [];
      pendientes.push(fichajeOffline);
      localStorage.setItem('fichajes-pendientes', JSON.stringify(pendientes));

      // Actualizar estado local para reflejar el fichaje
      setFichajes(prev => [...prev, {
        tipo,
        fecha: clientTimestamp,
        userId: { _id: user._id },
        _id: fichajeOffline.uuid
      }]);
      if (tipo === 'entrada') {
        setPuedeFicharEntrada(false);
        setPuedeFicharSalida(true);
      } else {
        setPuedeFicharSalida(false);
      }

      setMensajeExito(`📴 Fichaje de ${tipo} guardado offline (${pendientes.length} pendiente${pendientes.length > 1 ? 's' : ''}). Se sincronizará cuando haya conexión.`);
      setTimeout(() => setMensajeExito(""), 8000);
    } catch (offlineError) {
      setMensajeError("Error al guardar offline. Inténtalo de nuevo.");
      setTimeout(() => setMensajeError(""), 4000);
    }
  };

  const handleClickFichar = (tipo) => {
    if (tipo === "entrada") {
      if (confirmarEntrada) {
        handleFichar(tipo);
      } else {
        setConfirmarEntrada(true);
        timeoutEntrada.current = setTimeout(() => {
          setConfirmarEntrada(false);
          setMensajeError("No se realizó el fichaje de entrada. Debes pulsar dos veces para confirmar.");
          setTimeout(() => setMensajeError(""), 4000);
        }, 3000);
      }
    } else if (tipo === "salida") {
      if (confirmarSalida) {
        handleFichar(tipo);
      } else {
        setConfirmarSalida(true);
        timeoutSalida.current = setTimeout(() => {
          setConfirmarSalida(false);
          setMensajeError("No se realizó el fichaje de salida. Debes pulsar dos veces para confirmar.");
          setTimeout(() => setMensajeError(""), 4000);
        }, 3000);
      }
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Botón de sincronización manual */}
      {localStorage.getItem('fichajes-pendientes') && JSON.parse(localStorage.getItem('fichajes-pendientes') || '[]').filter(f => f.userId === user?._id).length > 0 && (
        <div className="lg:col-span-2 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800">
                📴 Tienes {JSON.parse(localStorage.getItem('fichajes-pendientes') || '[]').filter(f => f.userId === user?._id).length} fichaje(s) pendiente(s) de sincronizar
              </p>
            </div>
            <button
              onClick={sincronizarPendientes}
              disabled={!navigator.onLine}
              className="ml-4 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
            >
              {navigator.onLine ? '🔄 Sincronizar ahora' : '📴 Sin conexión'}
            </button>
          </div>
        </div>
      )}
      
      {/* Fichajes offline pendientes de validación o rechazados */}
      <div className="lg:col-span-2">
        <FichajesOfflineStatus />
      </div>

      {/* Panel de Fichaje */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">

        {/* Header */}
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{user?.nombre}</h2>
              <p className="text-gray-500 mt-1 text-sm">{user?.empresa?.nombre}</p>
            </div>
            {user?.empresa?.imagenUrl && (
              <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center p-2">
                <img
                  src={`${BACKEND_URL}${user.empresa.imagenUrl}`}
                  alt={user.empresa.nombre}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-200"></div>

        {/* Contenido - Imagen y botones */}
        <div className="p-6">
          <div className="flex gap-6 items-center">
            {/* Imagen del empleado */}
            <div className="flex-shrink-0">
              {user?.imagenPerfil || user?.imagenUrl ? (
                <img
                  src={`${BACKEND_URL}${user.imagenPerfil || user.imagenUrl}`}
                  alt={user.nombre}
                  className="w-32 h-32 rounded object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 rounded bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                  <i className="fas fa-user text-gray-400 text-5xl"></i>
                </div>
              )}
            </div>

            {/* Botones de fichaje */}
            {!jornadaRegistrada ? (
              <div className="flex-1 space-y-3">
                {/* Mensaje de no jornada asignada */}
                {(!user?.jornadasEffective || user.jornadasEffective.length === 0) && 
                 (!user?.jornadas || user.jornadas.length === 0) ? (
                  <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="fas fa-exclamation-triangle text-xl mt-0.5"></i>
                      <div>
                        <p className="font-semibold mb-1">No tienes jornada asignada</p>
                        <p className="text-sm">
                          Por favor, contacta con el administrador de tu empresa para que te asigne una jornada laboral.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {sincronizandoPendientes && (
                      <div className="w-full py-3 px-4 rounded-lg bg-blue-100 text-blue-800 text-center font-semibold text-sm animate-pulse mb-2">
                        <i className="fas fa-sync-alt fa-spin mr-2"></i>
                        Sincronizando fichajes offline pendientes...
                      </div>
                    )}
                    <button
                      onClick={() => handleClickFichar("entrada")}
                      disabled={!puedeFicharEntrada || sincronizandoPendientes}
                      className={`w-full py-3 px-4 rounded-lg font-semibold text-base transition-all ${
                        puedeFicharEntrada && !sincronizandoPendientes
                          ? confirmarEntrada
                            ? "bg-green-600 text-white animate-pulse"
                            : "bg-green-500 text-white hover:bg-green-600"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <i className="fas fa-sign-in-alt mr-2"></i>
                      {confirmarEntrada ? "Confirmar Entrada" : "Fichar Entrada"}
                    </button>

                    <button
                      onClick={() => handleClickFichar("salida")}
                      disabled={!puedeFicharSalida || sincronizandoPendientes}
                      className={`w-full py-3 px-4 rounded-lg font-semibold text-base transition-all ${
                        puedeFicharSalida && !sincronizandoPendientes
                          ? confirmarSalida
                            ? "bg-red-600 text-white animate-pulse"
                            : "bg-red-500 text-white hover:bg-red-600"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <i className="fas fa-sign-out-alt mr-2"></i>
                      {confirmarSalida ? "Confirmar Salida" : "Fichar Salida"}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <i className="fas fa-check-circle"></i>
                  <span className="text-sm">Su jornada diaria ha sido registrada correctamente</span>
                </div>
              </div>
            )}
          </div>

          {/* Mensajes */}
          {mensajeExito && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="fas fa-check-circle"></i>
              <span className="text-sm">{mensajeExito}</span>
            </div>
          )}

          {mensajeError && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="fas fa-exclamation-circle"></i>
              <span className="text-sm">{mensajeError}</span>
            </div>
          )}
        </div>

        {/* Jornada Laboral */}
        <div className="border-t border-gray-200 p-6 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Jornada Laboral</h3>
            {user?.jornadasEffective && user.jornadasEffective.length > 0 ? (
              <div className="space-y-4">
                {user.jornadasEffective.map((j) => (
                  <div key={j._id} className="p-2 border border-gray-100 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-700">{j.nombre}</div>
                      {!j.activo && <div className="text-xs text-red-500">Inactiva</div>}
                    </div>
                    {j.horario && j.horario.length > 0 ? (
                      <div className="space-y-2">
                        {j.horario.map((h, idx) => (
                          <div key={idx} className="flex items-center gap-3 border border-gray-200 p-3 rounded">
                            <i className="fas fa-clock text-gray-400 text-sm"></i>
                            <span className="text-gray-700 font-medium">
                              {h.dia}: {h.horaInicio} - {h.horaFin}
                              {h.descansoMinutos ? ` (Descanso: ${h.descansoMinutos} min)` : ""}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Sin horario definido</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No hay jornadas asignadas</div>
            )}
          </div>

          {/* Jornada Actual y Restante */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Jornada Actual
              </h4>
              <div className="border border-gray-300 p-3 rounded bg-gray-50">
                <p className="text-2xl font-semibold text-gray-800">
                  {Math.floor(tiempoTrabajadoMin / 60)}h{" "}
                  {tiempoTrabajadoMin % 60}m
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Jornada Restante
              </h4>
              <div className="border border-gray-300 p-3 rounded bg-gray-50">
                <p className="text-2xl font-semibold text-gray-800">
                  {Math.floor(tiempoRestanteMin / 60)}h{" "}
                  {tiempoRestanteMin % 60}m
                </p>
              </div>
            </div>
          </div>

          {/* Botones Solicitudes */}
          <div className="space-y-3">
            <button
              onClick={() => {
                setShowSolicitudes(!showSolicitudes);
                setSolicitudesViewMode('form');
              }}
              className="w-full bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-900 transition font-semibold flex items-center justify-center gap-2"
            >
              <i className="fas fa-plus"></i>
              Crear Solicitud
            </button>
            <button
              onClick={() => {
                setShowSolicitudes(!showSolicitudes);
                setSolicitudesViewMode('list');
              }}
              className="w-full bg-gray-700 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition font-semibold flex items-center justify-center gap-2"
            >
              <i className="fas fa-file-alt"></i>
              Ver Mis Solicitudes
            </button>
          </div>
        </div>
      </div>

      {/* Panel de Calendario */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="overflow-x-auto">
          <CalendarioFichajes />
        </div>
      </div>

      {/* Modal de Solicitudes */}
      {showSolicitudes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">Solicitudes</h3>
              <button
                onClick={() => setShowSolicitudes(false)}
                className="text-gray-500 hover:text-gray-700 transition"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            <div className="p-2 sm:p-6">
              <FichajesOfflineStatus />
              <SolicitudesTrabajador 
                viewMode={solicitudesViewMode}
                onViewModeChange={setSolicitudesViewMode}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
